/*
 * apiClient.js — Global authentication plumbing.
 *
 * Installs, as a side-effect at import time:
 *   - a window.fetch wrapper that injects "Authorization: Bearer <token>"
 *     on every call to /api/* (except /api/auth/login) and reacts to 401
 *     by clearing the stored token and bouncing the user back to login.
 *   - an axios request interceptor that does the same for any axios call.
 *
 * Also exports `downloadWithAuth(url, filename)` for file downloads that
 * previously relied on window.open (which cannot carry the token).
 */

import axios from 'axios';

const TOKEN_KEY = 'salonup_token';

function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

function isApiUrl(url) {
  return typeof url === 'string' && url.includes('/api/');
}

function isLoginUrl(url) {
  return typeof url === 'string' && url.includes('/api/auth/login');
}

function handle401() {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
  // Hard reload: AuthProvider will detect missing token and show LoginPage.
  if (typeof window !== 'undefined' && !window.__salonup_reloading__) {
    window.__salonup_reloading__ = true;
    window.location.reload();
  }
}

// ============ fetch monkey-patch (same tab only) ============
if (typeof window !== 'undefined' && !window.__salonup_fetch_patched__) {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const nextInit = { ...(init || {}) };
    let url = typeof input === 'string' ? input : (input && input.url) || '';

    if (isApiUrl(url) && !isLoginUrl(url)) {
      const token = getToken();
      if (token) {
        const headers = new Headers(nextInit.headers || (typeof input !== 'string' && input.headers) || {});
        if (!headers.has('Authorization')) {
          headers.set('Authorization', `Bearer ${token}`);
        }
        nextInit.headers = headers;
      }
    }

    const response = await originalFetch(input, nextInit);
    if (response && response.status === 401 && isApiUrl(url) && !isLoginUrl(url)) {
      handle401();
    }
    return response;
  };
  window.__salonup_fetch_patched__ = true;
}

// ============ axios interceptors ============
axios.interceptors.request.use((config) => {
  const url = config.url || '';
  if (isApiUrl(url) && !isLoginUrl(url)) {
    const token = getToken();
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization && !config.headers.authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error && error.response && error.response.status;
    const url = (error && error.config && error.config.url) || '';
    if (status === 401 && isApiUrl(url) && !isLoginUrl(url)) {
      handle401();
    }
    return Promise.reject(error);
  }
);

// ============ Authenticated file download ============
export async function downloadWithAuth(url, filename) {
  const token = getToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(url, { headers });
  if (!res.ok) {
    if (res.status === 401) handle401();
    throw new Error(`Falha no download (${res.status})`);
  }
  const blob = await res.blob();

  // Try to pick a sensible filename if none was given.
  let finalName = filename;
  if (!finalName) {
    const cd = res.headers.get('content-disposition') || '';
    const m = /filename\*?=(?:UTF-8'')?"?([^;"']+)"?/i.exec(cd);
    if (m && m[1]) {
      try { finalName = decodeURIComponent(m[1]); } catch { finalName = m[1]; }
    }
  }
  if (!finalName) {
    try { finalName = url.split('/').pop() || 'download'; } catch { finalName = 'download'; }
  }

  const objUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = finalName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(objUrl), 500);
}
